import { useId, useState, type ReactNode } from "react";
import { BookOpen, CheckCircle, Clock, XCircle } from "lucide-react";
import { Link } from "wouter";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  getGetMySubmissionsQueryKey,
  useGetMySubmissions,
  useSubmitQuote,
  type QuoteSubmissionEra,
  type SubmissionWithVotes,
} from "@workspace/api-client-react";
import { useUserProfile } from "@/contexts/UserProfileContext";
import { useAuth } from "@/contexts/AuthContext";
import { useAuthEnabled } from "@/lib/authQuery";
import { usePlausible } from "@/hooks/usePlausible";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface SubmitQuoteCardProps {
  showHistory?: boolean;
  communityPreview?: boolean;
  renderHistory?: (submissions: SubmissionWithVotes[]) => ReactNode;
}

/** One submission flow for Profile and Community; publication is decided by the API. */
export function SubmitQuoteCard(props: SubmitQuoteCardProps) {
  const { currentUser } = useAuth();
  // Changing accounts must also discard drafts and in-flight local feedback.
  return currentUser ? <SubmissionForm key={currentUser.uid} {...props} /> : null;
}

function SubmissionForm({
  showHistory = true,
  communityPreview = false,
  renderHistory,
}: SubmitQuoteCardProps) {
  const fieldId = useId();
  const queryClient = useQueryClient();
  const authed = useAuthEnabled();
  const mySubmissionsQuery = useGetMySubmissions({
    query: {
      queryKey: getGetMySubmissionsQueryKey(), enabled: authed,
      refetchInterval: query => query.state.data?.submissions.some(sub => sub.status === "pending") ? 30_000 : false,
    },
  });
  const submitMutation = useSubmitQuote();
  const { profile } = useUserProfile();
  const { trackEvent } = usePlausible();
  const [text, setText] = useState("");
  const [author, setAuthor] = useState("");
  const [source, setSource] = useState("");
  const [era, setEra] = useState<QuoteSubmissionEra | "">();
  const [isUserAuthor, setIsUserAuthor] = useState(false);
  const [submittedStatus, setSubmittedStatus] = useState<"approved" | "pending" | "rejected" | null>(null);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  const submissions = mySubmissionsQuery.data?.submissions ?? [];
  const nextSubmitAt = mySubmissionsQuery.data?.nextSubmitAt ?? null;
  const isRateLimited = nextSubmitAt ? new Date(nextSubmitAt) > new Date() : false;
  const daysRemaining = isRateLimited
    ? Math.max(1, Math.ceil((new Date(nextSubmitAt!).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : 0;
  const displayUsername = profile?.username ?? "you";
  const characterCount = Array.from(text.trim()).length;

  const handleIsUserAuthorChange = (checked: boolean) => {
    setIsUserAuthor(checked);
    if (checked) setAuthor("");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!authed || submitMutation.isPending || characterCount > 600) return;
    setSubmissionError(null);
    submitMutation.mutate(
      {
        data: {
          text,
          ...(isUserAuthor ? {} : { author }),
          source: source.trim() || null,
          era: era || null,
          isUserAuthor,
        },
      },
      {
        onSuccess: (data) => {
          trackEvent("Submission Sent");
          setSubmittedStatus(data.submission.status as "approved" | "pending" | "rejected");
          setText("");
          setAuthor("");
          setSource("");
          setEra("");
          setIsUserAuthor(false);
          queryClient.invalidateQueries({ queryKey: getGetMySubmissionsQueryKey() });
        },
        onError: (err: unknown) => {
          if (err instanceof ApiError && err.status === 409) {
            const data = err.data as Record<string, unknown> | null;
            if (data?.reason === "duplicate") {
              setSubmissionError("This quote is already in the library");
              toast.error("This quote is already in the library");
              return;
            }
          }
          if (err instanceof ApiError && typeof (err.data as Record<string, unknown> | null)?.error === "string") {
            setSubmissionError((err.data as { error: string }).error);
            toast.error((err.data as { error: string }).error);
            return;
          }
          setSubmissionError("Failed to submit — please try again");
          toast.error("Failed to submit — please try again");
        },
      },
    );
  };

  if (mySubmissionsQuery.isLoading) {
    return (
      <div aria-label="Loading submission form" className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm">
        <div className="mb-4 h-6 w-40 animate-pulse rounded-xl bg-stone-100" />
        <div className="h-32 animate-pulse rounded-xl bg-stone-100" />
      </div>
    );
  }

  return (
    <div data-testid="card-submit-quote" className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm">
      <div className="mb-1 flex items-center gap-3">
        <BookOpen className="h-5 w-5 text-stone-400" />
        <h3 className="text-xl font-semibold text-stone-900">Submit a quote</h3>
      </div>
      <p className="mb-6 text-sm text-stone-500">
        Know a quote that isn&apos;t in the library yet? Submit it for review — one every 3 days.
      </p>

      <div aria-live="polite">
        {isRateLimited ? (
          <div data-testid="status-submission-cooldown" className="flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4">
            <Clock className="h-5 w-5 shrink-0 text-amber-500" />
            <p className="text-sm text-amber-800">
              Your submission is in review. You can submit again in{" "}
              <span className="font-semibold">{daysRemaining === 1 ? "1 day" : `${daysRemaining} days`}</span>.
            </p>
          </div>
        ) : submittedStatus === "approved" ? (
          <div data-testid="status-submission-approved" className="flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4">
            <CheckCircle className="h-5 w-5 shrink-0 text-emerald-600" />
            <p className="text-sm font-medium text-emerald-800">Your quote was approved!</p>
          </div>
        ) : submittedStatus === "pending" ? (
          <div data-testid="status-submission-pending" className="flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4">
            <Clock className="h-5 w-5 shrink-0 text-amber-500" />
            <p className="text-sm font-medium text-amber-800">Submitted for review — thanks! We&apos;ll check the authorship and let you know.</p>
          </div>
        ) : submittedStatus === "rejected" ? (
          <div data-testid="status-submission-rejected" className="flex items-center gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-4">
            <XCircle className="h-5 w-5 shrink-0 text-red-500" />
            <p className="text-sm font-medium text-red-800">
              Your quote couldn&apos;t be added — {showHistory ? "see details below." : (
                <Link href="/profile" className="underline underline-offset-2">see details in your submission history.</Link>
              )}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="form-submit-quote">
            <div className="space-y-1.5">
              <Label htmlFor={`${fieldId}-text`} className="text-sm text-stone-600">Quote text</Label>
              <Textarea id={`${fieldId}-text`} data-testid="input-quote-text" placeholder="Enter the full quote text…"
                value={text} onChange={(e) => setText(e.target.value)} rows={4} required minLength={5}
                aria-describedby={`${fieldId}-count`} className="resize-none rounded-xl" />
              <p id={`${fieldId}-count`} data-testid="text-quote-character-count" className={`text-xs ${characterCount > 600 ? "text-red-600" : "text-stone-500"}`}>
                {characterCount}/600 characters
              </p>
            </div>
            <div className="flex items-center gap-2.5">
              <input id={`${fieldId}-original`} data-testid="input-is-user-author" type="checkbox" checked={isUserAuthor}
                onChange={(e) => handleIsUserAuthorChange(e.target.checked)}
                className="h-4 w-4 cursor-pointer rounded border-stone-300 text-stone-900 accent-stone-900" />
              <Label htmlFor={`${fieldId}-original`} className="cursor-pointer select-none text-sm text-stone-600">I am the author of this quote</Label>
            </div>
            {isUserAuthor ? (
              <div className="space-y-1.5">
                <Label className="text-sm text-stone-600">Author</Label>
                <div className="flex items-center rounded-xl border border-stone-200 bg-stone-50 px-3 py-2.5">
                  <span data-testid="text-submission-author" className="text-sm text-stone-700">{displayUsername}</span>
                </div>
                <p className="text-xs leading-relaxed text-stone-400">
                  {communityPreview
                    ? "Approved original quotes are collected for the Community feed when it opens."
                    : "Your quote will appear in the community section."} If AI finds it&apos;s already attributed to a known person, it won&apos;t be approved.
                </p>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label htmlFor={`${fieldId}-author`} className="text-sm text-stone-600">Author</Label>
                <Input id={`${fieldId}-author`} data-testid="input-quote-author" placeholder="e.g. Marcus Aurelius"
                  value={author} onChange={(e) => setAuthor(e.target.value)} required className="rounded-xl" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor={`${fieldId}-source`} className="text-sm text-stone-600">Source <span className="font-normal text-stone-400">(optional)</span></Label>
              <Input id={`${fieldId}-source`} data-testid="input-quote-source" placeholder="e.g. Meditations, Book II"
                value={source} onChange={(e) => setSource(e.target.value)} className="rounded-xl" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`${fieldId}-era`} className="text-sm text-stone-600">Era <span className="font-normal text-stone-400">(optional)</span></Label>
              <Select value={era ?? ""} onValueChange={(v) => setEra(v as QuoteSubmissionEra)}>
                <SelectTrigger id={`${fieldId}-era`} data-testid="select-quote-era" className="rounded-xl"><SelectValue placeholder="Select an era…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ancient">Ancient</SelectItem>
                  <SelectItem value="medieval">Medieval</SelectItem>
                  <SelectItem value="enlightenment">Enlightenment</SelectItem>
                  <SelectItem value="romantic">Romantic</SelectItem>
                  <SelectItem value="modern">Modern</SelectItem>
                  <SelectItem value="contemporary">Contemporary</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {submissionError && (
              <p role="alert" data-testid="text-submission-error" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{submissionError}</p>
            )}
            <div className="pt-1">
              <button type="submit" data-testid="button-submit-quote"
                disabled={!authed || submitMutation.isPending || !text.trim() || characterCount > 600 || (!isUserAuthor && !author.trim())}
                className="rounded-xl bg-stone-900 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-stone-700 disabled:opacity-50">
                {submitMutation.isPending ? "Submitting…" : "Submit for review"}
              </button>
            </div>
          </form>
        )}
      </div>
      {showHistory && renderHistory?.(submissions)}
    </div>
  );
}
